import classNames from "classnames";
import { useRef } from "react";
import LoadingSpinner from "../../LoadingSpinner/LoadingSpinner";
import "./PaginatedList.css";
import { type QueryKey, useInfiniteQuery } from "@tanstack/react-query";

function hasElementReachedTheBottom(element: HTMLDivElement | null, buffer: number) {
    if (!element) {
        return false;
    }
    return element.scrollTop > element.scrollHeight - element.offsetHeight - buffer;
}

type PaginatedListProps<CursorType, ItemType> = {
    className?: string;
    itemParentClassName?: string;
    queryKey: QueryKey;
    queryForItems: ({
        size,
        page,
    }: {
        size: number;
        page: CursorType;
    }) => Promise<{ data: ItemType[]; total: number; cursor?: CursorType }>;
    pageLength: number;
    renderItem: (item: ItemType, index: number) => React.JSX.Element | null;
    renderError: () => React.JSX.Element;
};

/*
 This seems like a legacy way to query stuff. Maybe we should create something like
 "useInfiniteGet" (resembling useGet) and refactor this component to use it so that
 usage of this component is simplified.
*/
function PaginatedList<CursorType, ItemType>(
    props: PaginatedListProps<CursorType, ItemType>,
) {
    const {
        className,
        queryForItems,
        itemParentClassName,
        queryKey,
        pageLength,
        renderItem,
        renderError,
    } = props;
    const scrollElementRef = useRef<HTMLDivElement>(null);

    const {
        data: searchResults,
        hasNextPage,
        fetchNextPage,
        isFetching,
        error,
    } = useInfiniteQuery<{ data: ItemType[]; total: number; cursor?: CursorType }>({
        queryKey,
        queryFn: async ({ pageParam }) => {
            return queryForItems({
                size: pageLength,
                page: pageParam as CursorType,
            });
        },
        getNextPageParam: (lastPage, allPages) => {
            const allPagesData = allPages.flatMap((page) => page.data);
            const lastPageCursor = lastPage.cursor;

            if (lastPage.total <= allPagesData.length) {
                return undefined;
            }

            if (lastPageCursor) {
                return lastPageCursor;
            }

            return pageLength * allPages.length;
        },
        initialPageParam: 0,
        staleTime: 0,
    });

    function onScroll() {
        if (isFetching) return;

        if (hasElementReachedTheBottom(scrollElementRef.current, 150) && hasNextPage) {
            fetchNextPage();
        }
    }

    return (
        <div
            className={classNames("paginated-list", className)}
            ref={scrollElementRef}
            onScroll={onScroll}
        >
            <div className={classNames("paginated-list__items", itemParentClassName)}>
                {searchResults?.pages
                    .flatMap((page) => page.data)
                    .map((item, index) => renderItem(item, index))}
                {isFetching && (
                    <div className="paginated-list--loading">
                        <LoadingSpinner />
                    </div>
                )}
                {error && renderError()}
            </div>
        </div>
    );
}

export default PaginatedList;
